import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Compass, Ship, Anchor, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function RoleSelect() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(null);
  const [askInvite, setAskInvite] = useState(false);
  const [invite, setInvite] = useState("");
  const [inviteError, setInviteError] = useState("");

  useEffect(() => {
    if (!loading && !user) navigate("/", { replace: true });
    if (!loading && user?.role) navigate(user.role === "guide" ? "/guide" : "/map", { replace: true });
  }, [user, loading, navigate]);

  const choose = async (role, inviteCode) => {
    if (role === "guide" && !inviteCode) { setAskInvite(true); return; }
    setSaving(role);
    setInviteError("");
    try {
      const res = await api.post("/auth/role", { role, invite_code: inviteCode || undefined });
      setUser(res.data);
      toast.success(role === "guide" ? "Welcome aboard, Guide!" : "Set sail, Explorer!");
      navigate(role === "guide" ? "/guide" : "/map", { replace: true });
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = (d && d.message) || (typeof d === "string" ? d : "Could not save your role. Try again.");
      if (role === "guide") setInviteError(msg); else toast.error(msg);
      setSaving(null);
    }
  };

  const tryFree = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/?trial=1";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const cards = [
    {
      role: "explorer",
      icon: Ship,
      title: "Explorer",
      tag: "Student",
      desc: "Set out across the world map, complete Trials, earn Horizon Points, and rise through the Fleet rankings.",
      accent: "primary",
    },
    {
      role: "guide",
      icon: Anchor,
      title: "Guide",
      tag: "Teacher",
      desc: "Create Expeditions, share join codes, track standards mastery, and steer the leaderboard.",
      accent: "teal",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-16">
      <Compass className="w-10 h-10 text-primary hq-float mb-6" />
      <h1 className="font-display text-4xl sm:text-5xl text-center tracking-tight">Choose your path</h1>
      <p className="text-muted-foreground mt-3 mb-12 text-center">How will you journey through HorizonQuest?</p>

      <div className="grid gap-6 md:grid-cols-2 w-full max-w-3xl">
        {cards.map((c, i) => (
          <button
            key={c.role}
            data-testid={`role-${c.role}-btn`}
            disabled={!!saving}
            onClick={() => choose(c.role)}
            style={{ animationDelay: `${0.1 * (i + 1)}s` }}
            className={`hq-fade-up text-left hq-glass rounded-2xl p-8 border transition-all duration-200 hover:-translate-y-1 disabled:opacity-60 ${
              c.accent === "primary"
                ? "border-primary/25 hover:border-primary/60 hover:hq-glow-gold"
                : "border-[#22D3EE]/25 hover:border-[#22D3EE]/60 hover:hq-glow-teal"
            }`}
          >
            <div className="flex items-center justify-between mb-6">
              <c.icon className={`w-10 h-10 ${c.accent === "primary" ? "text-primary" : "text-[#22D3EE]"}`} />
              <span className="text-xs font-mono-data uppercase tracking-widest text-muted-foreground">{c.tag}</span>
            </div>
            <h2 className="font-display text-3xl mb-2">{c.title}</h2>
            <p className="text-slate-400 text-sm leading-relaxed">{c.desc}</p>
            {c.role === "guide" && <p className="text-xs text-[#22D3EE] mt-3 flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Requires an invite code</p>}
            {saving === c.role && <Loader2 className="w-5 h-5 mt-4 animate-spin text-primary" />}
          </button>
        ))}
      </div>

      {askInvite && (
        <div data-testid="guide-invite-panel" className="hq-glass rounded-2xl p-6 border border-[#22D3EE]/40 w-full max-w-3xl mt-6 hq-fade-up">
          <h3 className="font-display text-2xl flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#22D3EE]" /> Enter your Guide invite code</h3>
          <p className="text-sm text-slate-400 mt-1">Full Guide accounts are unlocked with a single-use code from HorizonQuest.</p>
          <div className="flex flex-col sm:flex-row gap-2 mt-4">
            <input data-testid="guide-invite-input" value={invite} onChange={(e) => { setInvite(e.target.value.toUpperCase()); setInviteError(""); }} placeholder="e.g. 7K2QX9BD" maxLength={8}
              className="flex-1 h-11 rounded-lg bg-white/5 border border-white/15 px-4 font-mono-data tracking-widest uppercase outline-none focus:border-[#22D3EE]" />
            <button data-testid="guide-invite-submit" disabled={!!saving || invite.trim().length < 4} onClick={() => choose("guide", invite.trim())}
              className="h-11 px-6 rounded-lg bg-[#22D3EE] text-[#04121f] font-medium hover:bg-[#67E8F9] disabled:opacity-50 transition-colors">
              {saving === "guide" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Unlock Guide account"}
            </button>
          </div>
          {inviteError && <p data-testid="guide-invite-error" className="text-sm text-rose-400 mt-3">{inviteError}</p>}
          <p className="text-xs text-muted-foreground mt-4">No code yet? <button data-testid="guide-try-free-btn" onClick={tryFree} className="text-primary hover:underline">Start a free 14-day trial</button> instead — no code needed.</p>
        </div>
      )}
    </div>
  );
}
